import { useState, useCallback, useRef, useEffect, useMemo } from "react"
import dayjs from "dayjs"
import {
  useForecastControllerRunForecastMutation,
  type ForecastItemDto,
} from "~/ui-rtk/api/forecastApi"
import Button from "~/ui-rtk/components/ui/controls/Button"
import { useCubeQuery } from "~/ui-rtk/hooks/cube"
import {
  EDITABLE_COLUMNS,
  generateDefaultForecastData,
  toDisplayValue,
  fromDisplayValue,
  type EditableKey,
  type ColumnDef,
} from "./constants"

function formatMonth(dateStr: string) {
  const [year, month] = dateStr.split("-")
  return new Date(Number(year), Number(month) - 1).toLocaleDateString("en-US", {
    month: "short",
    year: "numeric",
  })
}

function formatDisplay(value: number, col: ColumnDef) {
  const display = toDisplayValue(value, col)
  const formatted =
    col.key === "daily_ad_spend"
      ? display.toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : display % 1 === 0
        ? display.toString()
        : display.toFixed(2)
  const prefix = col.prefix ?? ""
  const suffix = col.suffix ?? ""
  return `${prefix}${formatted}${suffix}`
}

function PencilIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
      <path d="m15 5 4 4" />
    </svg>
  )
}

function EditableCell({
  value,
  col,
  onChange,
  onApplyAll,
}: {
  value: number
  col: ColumnDef
  onChange: (rawValue: string) => void
  onApplyAll: (rawValue: string) => void
}) {
  const [open, setOpen] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => {
        inputRef.current?.focus()
        inputRef.current?.select()
      })
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    const handleClickOutside = (e: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node)
      ) {
        setOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [open])

  const getInputValue = () => inputRef.current?.value ?? ""

  const handleApply = () => {
    onChange(getInputValue())
    setOpen(false)
  }

  const handleApplyAll = () => {
    onApplyAll(getInputValue())
    setOpen(false)
  }

  const handleCancel = () => {
    setOpen(false)
  }

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="group flex items-center gap-1.5 px-2 py-1 -mx-2 rounded-system-xs hover:bg-system-blue-700/40 transition-colors w-full text-left"
      >
        <span className="text-sm text-white">{formatDisplay(value, col)}</span>
        <span className="text-system-gray-600 group-hover:text-system-brand-pink-500 transition-colors flex-shrink-0">
          <PencilIcon />
        </span>
      </button>

      {open && (
        <div
          ref={popoverRef}
          className="absolute z-20 top-full left-0 mt-1 w-56 p-3 rounded-system-sm border border-system-blue-600 bg-system-blue-800 shadow-deep animate-fadeIn"
        >
          <label className="block text-xs font-medium text-system-gray-400 mb-1.5">
            {col.label}
          </label>
          <div className="flex items-center gap-1.5 mb-3">
            {col.prefix && (
              <span className="text-sm text-system-gray-400">{col.prefix}</span>
            )}
            <input
              ref={inputRef}
              type="number"
              step={
                col.displayMultiplier
                  ? col.step * col.displayMultiplier
                  : col.step
              }
              defaultValue={toDisplayValue(value, col)}
              className="w-full px-2.5 py-1.5 rounded-system-xs border border-system-blue-600 bg-system-blue-900 text-white text-sm focus:outline-none focus:border-system-brand-pink-500 focus:ring-1 focus:ring-system-brand-pink-500/30"
              onKeyDown={e => {
                if (e.key === "Enter") handleApply()
                if (e.key === "Escape") handleCancel()
              }}
            />
            {col.suffix && (
              <span className="text-sm text-system-gray-400">{col.suffix}</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleApply}
              className="flex-1 px-3 py-1.5 rounded-system-xs text-xs font-medium bg-system-brand-pink-500 hover:bg-system-brand-pink-400 text-white transition-colors"
            >
              Apply
            </button>
            <button
              type="button"
              onClick={handleCancel}
              className="flex-1 px-3 py-1.5 rounded-system-xs text-xs font-medium border border-system-blue-600 text-system-gray-400 hover:text-white hover:border-system-gray-500 bg-transparent transition-colors"
            >
              Cancel
            </button>
          </div>
          <button
            type="button"
            onClick={handleApplyAll}
            className="w-full mt-2 px-3 py-1.5 rounded-system-xs text-xs font-medium border border-system-brand-pink-700 text-system-brand-pink-500 hover:bg-system-brand-pink-900/30 hover:text-system-brand-pink-400 transition-colors"
          >
            Apply to all months
          </button>
        </div>
      )}
    </div>
  )
}

export default function MarketPredictionPage() {
  const [data, setData] = useState<ForecastItemDto[]>(
    generateDefaultForecastData,
  )
  const [seededFromPrevYear, setSeededFromPrevYear] = useState(false)
  const [runForecast, { isLoading, isSuccess, isError }] =
    useForecastControllerRunForecastMutation()

  const prevYearQuery = useMemo(() => {
    const firstForecastMonth = dayjs(data[0]?.date)
    const start = firstForecastMonth.subtract(1, "year").startOf("month")
    const end = start.add(11, "month").endOf("month")
    return {
      measures: ["metrics.total_ad_spend"],
      timeDimensions: [
        {
          dimension: "metrics.date",
          granularity: "month" as const,
          dateRange: [start.format("YYYY-MM-DD"), end.format("YYYY-MM-DD")] as [
            string,
            string,
          ],
        },
      ],
    }
  }, [data])

  const { resultSet: prevYearResult, isLoading: isPrevYearLoading } =
    useCubeQuery(prevYearQuery)

  useEffect(() => {
    if (seededFromPrevYear || !prevYearResult) return

    const rows = prevYearResult.tablePivot() as Array<Record<string, unknown>>
    const spendByMonth = new Map<string, number>()
    for (const row of rows) {
      const dateStr = String(row["metrics.date.month"] ?? row["metrics.date"])
      if (!dateStr) continue
      const monthKey = dayjs(dateStr).format("MM")
      const value = Number(row["metrics.total_ad_spend"]) || 0
      spendByMonth.set(monthKey, value)
    }

    setData(prev =>
      prev.map(row => {
        const month = dayjs(row.date)
        const monthlySpend = spendByMonth.get(month.format("MM"))
        if (!monthlySpend || monthlySpend <= 0) return row
        const daysInMonth = month.daysInMonth()
        return { ...row, daily_ad_spend: monthlySpend / daysInMonth }
      }),
    )
    setSeededFromPrevYear(true)
  }, [prevYearResult, seededFromPrevYear])

  const handleValueChange = useCallback(
    (rowIndex: number, col: ColumnDef, rawValue: string) => {
      const num = parseFloat(rawValue)
      if (isNaN(num)) return
      const actual = fromDisplayValue(num, col)
      setData(prev =>
        prev.map((row, i) =>
          i === rowIndex ? { ...row, [col.key]: actual } : row,
        ),
      )
    },
    [],
  )

  const handleApplyAll = useCallback((col: ColumnDef, rawValue: string) => {
    const num = parseFloat(rawValue)
    if (isNaN(num)) return
    const actual = fromDisplayValue(num, col)
    setData(prev => prev.map(row => ({ ...row, [col.key]: actual })))
  }, [])

  const handleSubmit = async () => {
    await runForecast({ data }).unwrap()
  }

  const handleDownload = async () => {
    const baseUrl = import.meta.env.VITE_API_BASE_URL || ""
    const res = await fetch(`${baseUrl}/forecast/download`, {
      credentials: "include",
    })
    if (!res.ok) return
    const blob = await res.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = "forecast.csv"
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="p-6 max-w-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Market Prediction</h1>
          <p className="text-sm text-system-gray-400 mt-1">
            {isPrevYearLoading
              ? "Loading prior-year ad spend..."
              : "Click the pencil icon to edit a value. After editing, you can apply it to all months."}
          </p>
        </div>
        <Button
          onClick={handleSubmit}
          isLoading={isLoading}
          disabled={isLoading}
          variant={{ variant: "solid", color: "pink" }}
          size="md"
        >
          Run Forecast
        </Button>
      </div>

      {isSuccess && (
        <div className="mb-4 p-3 rounded-system-sm bg-system-success-900/30 border border-system-success-700 text-system-success-300 text-sm flex items-center justify-between">
          <span>
            Forecast triggered successfully. Results will be available shortly.
          </span>
          <button
            type="button"
            onClick={handleDownload}
            className="ml-4 px-3 py-1 rounded-system-xs text-xs font-medium border border-system-success-700 text-system-success-300 hover:bg-system-success-900/30 transition-colors whitespace-nowrap"
          >
            Download CSV
          </button>
        </div>
      )}
      {isError && (
        <div className="mb-4 p-3 rounded-system-sm bg-system-error-900/30 border border-system-error-700 text-system-error-300 text-sm">
          Failed to trigger forecast. Please try again.
        </div>
      )}

      {/* Input Table */}
      <div className="overflow-x-auto rounded-system-sm border border-system-blue-600">
        <table className="w-full">
          <thead>
            <tr className="bg-system-blue-800 border-b border-system-blue-600">
              <th className="text-left text-xs font-semibold text-system-gray-400 uppercase tracking-wide px-4 py-3 w-28">
                Month
              </th>
              {EDITABLE_COLUMNS.map(col => (
                <th
                  key={col.key}
                  className="text-left text-xs font-semibold text-system-gray-400 uppercase tracking-wide px-3 py-3"
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((row, rowIndex) => (
              <tr
                key={row.date}
                className="border-b border-system-blue-700/50 hover:bg-system-blue-800/40 transition-colors"
              >
                <td className="px-4 py-2">
                  <span className="text-sm font-medium text-white">
                    {formatMonth(row.date)}
                  </span>
                </td>
                {EDITABLE_COLUMNS.map(col => (
                  <td key={col.key} className="px-3 py-1">
                    <EditableCell
                      value={row[col.key as EditableKey]}
                      col={col}
                      onChange={raw => handleValueChange(rowIndex, col, raw)}
                      onApplyAll={raw => handleApplyAll(col, raw)}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
